import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Stack,
  MenuItem,
  Select,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
  Menu,
  Divider,
} from "@mui/material";
import Image from "react-cool-img";
import { useTranslation } from "react-i18next";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

import {
  AddAPhoto as AddAPhotoIcon,
  AutoAwesome as AIIcon,
  FreeBreakfast as BreakfastIcon,
  LunchDining as LunchIcon,
  DinnerDining as DinnerIcon,
  LocalCafe as SnackIcon,
  Restaurant as OtherIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import CreatableSelect from "react-select/creatable";
import { nanoid } from "nanoid";
import { useRecoilValue } from "recoil";
import StandardButton from "../../../../components/StandardButton";
import { consola } from "consola";
import MDEditor from "@uiw/react-md-editor";
import { getChatGPTResponse } from "../../../../data/GlobalData";
import { RecipeMacroCalculationMessage } from "../../../../data/ChatGPTMessages";
import { DeleteButton } from "../../../../atoms/EditButton";
import units from "../../../../data/Units";
import { myParseFloat } from "../../../../data/Library";
import fileUploadLimitSelector from "../../../../atoms/fileUploadLimitSelector";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";
import useEntitlements from "../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../components/organisms/UpgradePrompt";

const RecipeDialog = ({ open, recipe, editIngredient, onClose }) => {
  const { t } = useTranslation();
  const fileUploadLimit = useRecoilValue(fileUploadLimitSelector);
  const organizationId = useRecoilValue(organizationIdSelector);
  const { hasFeature, organizationData } = useEntitlements();
  const canUseMealPlanAi = hasFeature("meal_plan_data_entry_assistance");

  // CRITICAL: Always initialize with recipe.id if available, otherwise generate new ID
  // This prevents duplicates when editing - we MUST use the recipe's ID when editing
  const [id, setId] = React.useState(() => {
    // If recipe prop has an ID, use it (editing mode)
    // Otherwise generate new ID (creating mode)
    return recipe?.id || nanoid();
  });
  const [name, setName] = React.useState(recipe?.name || "");
  const [mealType, setMealType] = React.useState(recipe?.mealType || "dinner");
  const [isPublic, setIsPublic] = React.useState(recipe?.isPublic || false);
  const [description, setDescription] = React.useState(
    recipe?.description || "",
  );
  const [instructions, setInstructions] = React.useState(() => {
    if (!recipe) return [];
    if (Array.isArray(recipe.instructions)) {
      return recipe.instructions;
    } else if (
      typeof recipe.instructions === "string" &&
      recipe.instructions.trim() !== ""
    ) {
      return [{ instruction: recipe.instructions }];
    } else {
      return [];
    }
  });

  const [loading, setLoading] = React.useState(false);

  const [servings, setServings] = React.useState(recipe?.servings || 1);
  const [kCals, setkCals] = React.useState(recipe?.kCals || 0);
  const [proteins, setProteins] = React.useState(recipe?.proteins || 0);
  const [carbs, setCarbs] = React.useState(recipe?.carbs || 0);
  const [fats, setFats] = React.useState(recipe?.fats || 0);
  const [fiber, setFiber] = React.useState(recipe?.fiber || 0);

  const [imageUrl, setImageUrl] = React.useState(null);
  const [objectURL, setObjectURL] = React.useState(recipe?.imageUrl || null);

  const [ingredients, setIngredients] = React.useState(
    recipe?.ingredients || [],
  );
  const [isCalculatingMacros, setIsCalculatingMacros] = React.useState(false);

  // Lazy loading state for ingredients
  const [lazyIngredients, setLazyIngredients] = React.useState([]);
  const [isLoadingIngredients, setIsLoadingIngredients] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [hasSearched, setHasSearched] = React.useState(false);

  // Search ingredients on demand instead of loading all
  const searchIngredients = React.useCallback(
    async (term) => {
      if (!organizationId || term.length < 2) {
        setLazyIngredients([]);
        return;
      }

      setIsLoadingIngredients(true);
      try {
        // Only load ingredients that match the search term
        const ingredientsRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("ingredients")
          .where("name", ">=", term)
          .where("name", "<=", term + "\uf8ff")
          .limit(20); // Limit to 20 results for performance

        const snapshot = await ingredientsRef.get();
        const results = snapshot.docs.map((doc) => doc.data());
        setLazyIngredients(results);
        setHasSearched(true);
      } catch (error) {
        console.error("Error searching ingredients:", error);
        setLazyIngredients([]);
      } finally {
        setIsLoadingIngredients(false);
      }
    },
    [organizationId],
  );

  // Debounced search to avoid excessive API calls
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchIngredients(searchTerm);
      } else {
        setLazyIngredients([]);
        setHasSearched(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchIngredients]);

  // Convert lazy ingredients to select options
  const selectIngredients = React.useMemo(() => {
    return lazyIngredients.map((ingredient) => ({
      value: ingredient.id,
      label: ingredient.name,
      ingredient: ingredient,
    }));
  }, [lazyIngredients]);

  // Track the previous recipe ID and dialog open state to detect changes
  const prevRecipeId = React.useRef(null);
  const prevOpen = React.useRef(open);
  
  // CRITICAL: Update state when recipe prop changes or dialog opens/closes
  // This ensures we always use the correct recipe ID when editing
  React.useEffect(() => {
    const currentRecipeId = recipe?.id;
    const recipeChanged = currentRecipeId !== prevRecipeId.current;
    const dialogJustOpened = !prevOpen.current && open;
    const dialogJustClosed = prevOpen.current && !open;
    
    // Update if:
    // 1. Recipe ID changed
    // 2. Dialog just opened (need to sync state with recipe prop)
    // 3. Dialog just closed (reset for next time)
    const shouldUpdate = recipeChanged || dialogJustOpened || dialogJustClosed;

    if (shouldUpdate) {
      console.log("RecipeDialog: Updating state", {
        currentRecipeId,
        prevRecipeId: prevRecipeId.current,
        recipeChanged,
        dialogJustOpened,
        dialogJustClosed,
        hasRecipe: !!recipe,
        open,
        currentStateId: id
      });

      if (dialogJustClosed) {
        // Dialog closed - reset tracking
        prevRecipeId.current = null;
        prevOpen.current = open;
        return;
      }

      prevRecipeId.current = currentRecipeId;
      prevOpen.current = open;

      if (!recipe) {
        // Creating new recipe - generate new ID
        console.log("RecipeDialog: Creating new recipe with new ID");
        const newId = nanoid();
        setId(newId);
        console.log("RecipeDialog: Generated new ID:", newId);
        setName("");
        setMealType("dinner");
        setIsPublic(false);
        setDescription("");
        setInstructions([]);
        setkCals(0);
        setServings(1);
        setProteins(0);
        setCarbs(0);
        setFats(0);
        setFiber(0);
        setIngredients([]);
        setObjectURL(null);
        setImageUrl(null);
      } else {
        // Editing existing recipe - CRITICAL: ALWAYS use recipe.id to prevent duplicates
        if (recipe.id) {
          console.log("RecipeDialog: Editing recipe - setting ID to:", recipe.id, {
            currentStateId: id,
            recipeId: recipe.id,
            idsMatch: id === recipe.id
          });
          // FORCE update the ID even if it seems correct - this prevents race conditions
          setId(recipe.id);
        } else {
          // Fallback: if recipe has no ID, generate one (shouldn't happen for existing recipes)
          console.error("RecipeDialog: CRITICAL - Recipe passed without an ID!", recipe);
          const fallbackId = nanoid();
          console.warn("RecipeDialog: Generating fallback ID:", fallbackId);
          setId(fallbackId);
        }
        setName(recipe.name || "");
        setMealType(recipe.mealType || "dinner");
        setIsPublic(recipe.isPublic || false);
        setDescription(recipe.description || "");
        // Guard against instructions being a string (legacy data)
        if (Array.isArray(recipe.instructions)) {
          setInstructions(recipe.instructions);
        } else if (
          typeof recipe.instructions === "string" &&
          recipe.instructions.trim() !== ""
        ) {
          setInstructions([{ instruction: recipe.instructions }]);
        } else {
          setInstructions([]);
        }
        setkCals(recipe.kCals || 0);
        setServings(recipe.servings || 1);
        setProteins(recipe.proteins || 0);
        setCarbs(recipe.carbs || 0);
        setFats(recipe.fats || 0);
        setFiber(recipe.fiber || 0);
        setIngredients(recipe.ingredients || []);
        setObjectURL(recipe.imageUrl || null);
        setImageUrl(null);
      }
    }
  }, [recipe?.id, open, recipe]); // Don't include id to avoid infinite loops - we control it in the effect

  const handleCalculateMacros = async () => {
    if (!ingredients || ingredients.length === 0) {
      return;
    }

    if (!servings || servings <= 0) {
      alert("Please set a valid number of servings before calculating macros");
      return;
    }

    setIsCalculatingMacros(true);

    try {
      const messages = RecipeMacroCalculationMessage({
        ingredients,
        servings,
        recipeName: name,
      });

      const response = await getChatGPTResponse({ messages });

      if (response && response.response) {
        const nutritionData = JSON.parse(response.response[0].message.content);

        // Update the macro values
        setProteins(nutritionData.proteins.toFixed(2));
        setCarbs(nutritionData.carbs.toFixed(2));
        setFats(nutritionData.fats.toFixed(2));
        setkCals(nutritionData.kCals.toFixed(2));
        if (nutritionData.fiber != null) {
          setFiber(Number(nutritionData.fiber).toFixed(2));
        }
      }
    } catch (error) {
      console.error("Error calculating macros with AI:", error);
      alert(
        "Failed to calculate macros. Please try again or enter values manually.",
      );
    } finally {
      setIsCalculatingMacros(false);
    }
  };

  const [isGeneratingDescription, setIsGeneratingDescription] =
    React.useState(false);
  const [selectedDescriptionType, setSelectedDescriptionType] =
    React.useState("descriptive");
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  function formatWithAI2() {
    setLoading(true);
    getChatGPTResponse({
      messages: [
        {
          role: "system",
          content: "You are a markdown syntax expert that knows JSON format",
        },
        {
          role: "system",
          content: "You correct grammar and spelling mistakes where you can",
        },
        {
          role: "assistant",
          content: `{"description": "Here is a description", "ingredients": [{"name": "chicken breast", "amount": "1", "unit": "kg"}, {"name": "salt", "amount": "1", "unit": "tsp"}], "instructions": "Here are some instructions"}`,
        },
        {
          role: "user",
          content:
            "Respond with JSON. The description and instructions should be markdown.",
        },
        {
          role: "user",
          content:
            "If there are ingredients in the description, convert the ingredients to a separate JSON array of values.",
        },
        {
          role: "user",
          content:
            "Create a useful description of the recipe using the ingredients and the instructions and title. Add the description to the JSON as its own key and value.",
        },
        {
          role: "user",
          content:
            "The description is between the START and END. Convert the description to markdown. Generate headers and subheaders based on the description using markdown",
        },
        {
          role: "user",
          content: "START",
        },
        {
          role: "user",
          content: description,
        },
        {
          role: "user",
          content: "END",
        },
        {
          role: "user",
          content: "Respond with JSON",
        },
      ],
    })
      .then((response) => {
        if (response && response.response) {
          const description = response.response[0].message.content;
          const objectValue = JSON.parse(description);

          setName(objectValue.title);
          setDescription(objectValue.description);
          setInstructions(objectValue.instructions);
        }
        setLoading(false);
      })
      .catch((error) => {
        consola.log(error);
        setLoading(false);
      });
  }

  function generateDescriptionFromIngredients() {
    if (!ingredients || ingredients.length === 0) {
      alert(
        "Please add some ingredients first before generating a description.",
      );
      return;
    }

    setIsGeneratingDescription(true);

    const ingredientsList = ingredients
      .map((ing) => `${ing.amount} ${ing.unit || ""} ${ing.name}`)
      .join(", ");

    // Define description type prompts
    const descriptionTypes = {
      descriptive: {
        systemPrompt:
          "You are a culinary expert who writes clear, informative recipe descriptions.",
        userPrompt: `Based on these ingredients: ${ingredientsList}, write a clear recipe description. 

Instructions:
- First sentence should be a clear, descriptive title/hook
- Follow with 2-3 sentences describing the dish and its benefits
- Keep total under 100 words
- No markdown formatting - plain text only
- Be informative and straightforward

Format as plain text only.`,
      },
      clickbait: {
        systemPrompt:
          "You are a social media content creator who writes engaging, curiosity-driven recipe descriptions.",
        userPrompt: `Based on these ingredients: ${ingredientsList}, write an engaging recipe description. 

Instructions:
- First sentence should be a curiosity-driven hook that makes people want to know more
- Follow with 2-3 sentences explaining what makes this special
- Keep total under 100 words
- No markdown formatting - plain text only
- Use engaging, social media style language

Format as plain text only.`,
      },
      "benefit-driven": {
        systemPrompt:
          "You are a nutritionist who writes health-focused, benefit-driven recipe descriptions.",
        userPrompt: `Based on these ingredients: ${ingredientsList}, write a health-focused recipe description. 

Instructions:
- First sentence should highlight the main health benefit or fitness goal
- Follow with 2-3 sentences about nutritional benefits and who it's perfect for
- Keep total under 100 words
- No markdown formatting - plain text only
- Focus on health benefits, nutrition, and wellness

Format as plain text only.`,
      },
      "coach-voice": {
        systemPrompt:
          "You are a personal trainer/coach who writes friendly, authoritative recipe descriptions.",
        userPrompt: `Based on these ingredients: ${ingredientsList}, write a coach-style recipe description. 

Instructions:
- First sentence should be in a coach's voice, like "This is my go-to recipe for..."
- Follow with 2-3 sentences about why you recommend it to clients
- Keep total under 100 words
- No markdown formatting - plain text only
- Use first person and friendly, authoritative tone

Format as plain text only.`,
      },
      "problem-solution": {
        systemPrompt:
          "You are a solution-focused content creator who addresses common cooking/eating problems.",
        userPrompt: `Based on these ingredients: ${ingredientsList}, write a problem-solving recipe description. 

Instructions:
- First sentence should identify a common problem this recipe solves
- Follow with 2-3 sentences about how this recipe is the solution
- Keep total under 100 words
- No markdown formatting - plain text only
- Focus on solving busy lifestyles, time constraints, or dietary challenges

Format as plain text only.`,
      },
    };

    const selectedType =
      descriptionTypes[selectedDescriptionType] || descriptionTypes.descriptive;

    getChatGPTResponse({
      messages: [
        {
          role: "system",
          content: selectedType.systemPrompt,
        },
        {
          role: "user",
          content: selectedType.userPrompt,
        },
      ],
    })
      .then((response) => {
        if (response && response.response) {
          const generatedDescription = response.response[0].message.content;
          setDescription(generatedDescription);
        }
        setIsGeneratingDescription(false);
      })
      .catch((error) => {
        consola.log(error);
        setIsGeneratingDescription(false);
        alert("Failed to generate description. Please try again.");
      });
  }

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={() => {
        if (onClose) {
          onClose();
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "primary.main",
          color: "white",
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          {recipe ? "Edit Recipe" : "Create New Recipe"}
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={() => {
            if (onClose) {
              onClose();
            }
          }}
          sx={{ ml: 1 }}
        >
          ×
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Basic Information Card */}
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Basic Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <TextField
                      label="Recipe Name"
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Enter recipe name..."
                    />

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Meal Type
                        </Typography>
                        <Select
                          size="small"
                          fullWidth
                          variant="outlined"
                          value={mealType}
                          onChange={(event) => setMealType(event.target.value)}
                        >
                          <MenuItem value="breakfast">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <BreakfastIcon sx={{ fontSize: "1rem" }} />
                              {t("breakfast")}
                            </Box>
                          </MenuItem>
                          <MenuItem value="lunch">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <LunchIcon sx={{ fontSize: "1rem" }} />
                              {t("lunch")}
                            </Box>
                          </MenuItem>
                          <MenuItem value="dinner">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <DinnerIcon sx={{ fontSize: "1rem" }} />
                              {t("dinner")}
                            </Box>
                          </MenuItem>
                          <MenuItem value="snack">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <SnackIcon sx={{ fontSize: "1rem" }} />
                              {t("snack")}
                            </Box>
                          </MenuItem>
                          <MenuItem value="other">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <OtherIcon sx={{ fontSize: "1rem" }} />
                              {t("other")}
                            </Box>
                          </MenuItem>
                        </Select>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isPublic || false}
                              onChange={() => setIsPublic(!isPublic)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Public Recipe
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Available to all clients
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Grid>

                {/* Recipe Image */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Recipe Image
                  </Typography>
                  <Box
                    sx={{
                      border: "2px dashed",
                      borderColor: "grey.300",
                      borderRadius: 2,
                      p: 1.5,
                      textAlign: "center",
                      position: "relative",
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {objectURL ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <Image
                          src={objectURL}
                          alt="Recipe image"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: 8,
                          }}
                        />
                        <label>
                          <Button
                            variant="contained"
                            component="span"
                            size="small"
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              right: 4,
                              bgcolor: "rgba(0,0,0,0.7)",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
                              fontSize: "0.7rem",
                              minWidth: "auto",
                              px: 1,
                              py: 0.5,
                            }}
                          >
                            Change
                          </Button>
                          <input
                            hidden={true}
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                const image = e.target.files[0];

                                if (image.size > fileUploadLimit) {
                                  alert(
                                    `Image must be less than ${
                                      fileUploadLimit / 1000000
                                    }MB`,
                                  );
                                  return;
                                }
                                const objectURL = URL.createObjectURL(image);
                                setObjectURL(objectURL);
                                setImageUrl(image);
                              }
                            }}
                          />
                        </label>
                      </Box>
                    ) : (
                      <label>
                        <Stack
                          spacing={1}
                          alignItems="center"
                          sx={{ cursor: "pointer" }}
                        >
                          <AddAPhotoIcon
                            sx={{
                              fontSize: 32,
                              color: "primary.main",
                              "&:hover": { transform: "scale(1.1)" },
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Click to upload
                          </Typography>
                        </Stack>
                        <input
                          hidden={true}
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              const image = e.target.files[0];

                              if (image.size > fileUploadLimit) {
                                alert(
                                  `Image must be less than ${
                                    fileUploadLimit / 1000000
                                  }MB`,
                                );
                                return;
                              }
                              const objectURL = URL.createObjectURL(image);
                              setObjectURL(objectURL);
                              setImageUrl(image);
                            }
                          }}
                        />
                      </label>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Description Card */}
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
              >
                Description
              </Typography>

              <div data-color-mode="light">
                <MDEditor
                  preview="live"
                  placeholder="Enter healthy recipe description or video link here"
                  value={description}
                  onChange={(value) => setDescription(value)}
                  height={200}
                />
              </div>

              <Stack direction="row" spacing={1} alignItems="center" mt={2} flexWrap="wrap" useFlexGap>
                {canUseMealPlanAi ? (
                  <>
                <Button
                  variant="outlined"
                  onClick={formatWithAI2}
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <AIIcon />
                  }
                  disabled={loading || isGeneratingDescription}
                  size="small"
                >
                  {loading ? "Formatting..." : "Format with AI"}
                </Button>

                <Box sx={{ position: "relative" }}>
                  <Button
                    variant="outlined"
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    endIcon={<ArrowDropDownIcon />}
                    startIcon={
                      isGeneratingDescription ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AIIcon />
                      )
                    }
                    disabled={
                      isGeneratingDescription ||
                      loading ||
                      ingredients.length === 0
                    }
                    size="small"
                    color="secondary"
                  >
                    {isGeneratingDescription
                      ? "Generating..."
                      : "Generate from Ingredients"}
                  </Button>

                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    PaperProps={{
                      sx: { minWidth: 200 },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        px: 2,
                        py: 1,
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      Description Style
                    </Typography>
                    <Divider />

                    <MenuItem
                      onClick={() => {
                        setSelectedDescriptionType("descriptive");
                        setMenuAnchor(null);
                        generateDescriptionFromIngredients();
                      }}
                      selected={selectedDescriptionType === "descriptive"}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Descriptive
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Clear, informative description
                        </Typography>
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setSelectedDescriptionType("clickbait");
                        setMenuAnchor(null);
                        generateDescriptionFromIngredients();
                      }}
                      selected={selectedDescriptionType === "clickbait"}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Clickbait
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Curiosity-driven, engaging hook
                        </Typography>
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setSelectedDescriptionType("benefit-driven");
                        setMenuAnchor(null);
                        generateDescriptionFromIngredients();
                      }}
                      selected={selectedDescriptionType === "benefit-driven"}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Benefit-Driven
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Health & fitness focused
                        </Typography>
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setSelectedDescriptionType("coach-voice");
                        setMenuAnchor(null);
                        generateDescriptionFromIngredients();
                      }}
                      selected={selectedDescriptionType === "coach-voice"}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Coach Voice
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Personal trainer perspective
                        </Typography>
                      </Box>
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        setSelectedDescriptionType("problem-solution");
                        setMenuAnchor(null);
                        generateDescriptionFromIngredients();
                      }}
                      selected={selectedDescriptionType === "problem-solution"}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Problem-Solution
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Addresses common challenges
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>
                  </>
                ) : (
                  <UpgradePrompt
                    organizationData={organizationData}
                    minTier="pro"
                    variant="banner"
                    title="Upgrade for recipe AI assistance"
                    description="AI formatting and description generation are available on Pro."
                    featureName="meal_plan_data_entry_assistance"
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
          {/* Nutrition Card */}
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Nutrition Information
                </Typography>
                {canUseMealPlanAi ? (
                  <Button
                    variant="outlined"
                    onClick={handleCalculateMacros}
                    startIcon={
                      isCalculatingMacros ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AIIcon />
                      )
                    }
                    disabled={isCalculatingMacros || ingredients.length === 0}
                    size="small"
                  >
                    {isCalculatingMacros ? "Calculating..." : "Auto Calculate"}
                  </Button>
                ) : (
                  <UpgradePrompt
                    organizationData={organizationData}
                    minTier="pro"
                    variant="banner"
                    title="Upgrade for AI macro calculation"
                    featureName="meal_plan_data_entry_assistance"
                  />
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Servings
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={servings}
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      if (event.target.value === "") {
                        setServings(1);
                        return;
                      }
                      if (myParseFloat(event.target.value) < 1) {
                        setServings(1);
                        return;
                      }
                      setServings(myParseFloat(event.target.value));
                    }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Calories/serving
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={kCals}
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      if (event.target.value === "") {
                        setkCals(0);
                        return;
                      }
                      setkCals(myParseFloat(event.target.value));
                    }}
                  />
                </Grid>

                <Grid item xs={4} sm={4} md={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Protein (g)
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={proteins}
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      if (event.target.value === "") {
                        setProteins(0);
                        return;
                      }
                      setProteins(myParseFloat(event.target.value));
                    }}
                  />
                </Grid>

                <Grid item xs={4} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Carbs (g)
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={carbs}
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      if (event.target.value === "") {
                        setCarbs(0);
                        return;
                      }
                      setCarbs(myParseFloat(event.target.value));
                    }}
                  />
                </Grid>

                <Grid item xs={4} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Fats (g)
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={fats}
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      if (event.target.value === "") {
                        setFats(0);
                        return;
                      }
                      setFats(myParseFloat(event.target.value));
                    }}
                  />
                </Grid>

                <Grid item xs={4} sm={6} md={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Fiber (g)
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={fiber}
                    variant="outlined"
                    type="number"
                    onChange={(event) => {
                      if (event.target.value === "") {
                        setFiber(0);
                        return;
                      }
                      setFiber(myParseFloat(event.target.value));
                    }}
                  />
                </Grid>
              </Grid>

              {/* Nutrition Summary */}
              {(kCals > 0 ||
                proteins > 0 ||
                carbs > 0 ||
                fats > 0 ||
                fiber > 0) && (
                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`${Math.round(kCals)} cal/serving`}
                    color="primary"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`${Math.round(proteins)}g Protein`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    label={`${Math.round(carbs)}g Carbs`}
                    color="warning"
                    variant="outlined"
                  />
                  <Chip
                    label={`${Math.round(fats)}g Fats`}
                    color="info"
                    variant="outlined"
                  />
                  {fiber > 0 && (
                    <Chip
                      label={`${Math.round(fiber)}g Fiber`}
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
          {/* Ingredients Card */}
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Ingredients ({ingredients.length})
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIngredients([
                      ...ingredients,
                      {
                        id: nanoid(),
                        ingredientId: undefined,
                        name: "",
                        amount: "",
                        unit: null,
                      },
                    ]);
                  }}
                  color="primary"
                  size="small"
                >
                  Add Ingredient
                </Button>
              </Box>

              <Stack spacing={1.5}>
                {ingredients.map((ingredient) => {
                  const value = {
                    label: ingredient.name,
                    value: ingredient.ingredientId,
                  };
                  return (
                    <Paper
                      key={ingredient.id}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "grey.300",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <CreatableSelect
                          options={selectIngredients}
                          value={value}
                          maxMenuHeight={150}
                          placeholder="Type to search ingredients..."
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                          noOptionsMessage={({ inputValue }) =>
                            inputValue.length < 2
                              ? "Type 2+ characters to search ingredients"
                              : isLoadingIngredients
                                ? "Searching..."
                                : hasSearched
                                  ? "No ingredients found - press Enter to create new"
                                  : "Type to search ingredients"
                          }
                          isLoading={isLoadingIngredients}
                          onInputChange={(inputValue) => {
                            setSearchTerm(inputValue);
                          }}
                          filterOption={() => true} // Disable client-side filtering since we do server-side
                          onChange={(event) => {
                            ingredient.ingredientId = event.value;
                            ingredient.name = event.label;

                            if (event.ingredient?.unit) {
                              ingredient.unit = event.ingredient.unit;
                            }
                            if (event.ingredient?.proteins) {
                              ingredient.proteins = event.ingredient.proteins;
                            }
                            if (event.ingredient?.carbs) {
                              ingredient.carbs = event.ingredient.carbs;
                            }
                            if (event.ingredient?.fats) {
                              ingredient.fats = event.ingredient.fats;
                            }

                            setIngredients([...ingredients]);
                          }}
                          onCreateOption={async (inputValue) => {
                            const newIngredient = {
                              id: nanoid(),
                              name: inputValue,
                              createdOn: new Date(),
                            };

                            editIngredient(newIngredient).then(() => {
                              ingredient.ingredientId = newIngredient.id;
                              ingredient.name = newIngredient.name;
                              ingredient.unit = null;
                              ingredient.proteins = 0;
                              ingredient.carbs = 0;
                              ingredient.fats = 0;
                              setIngredients([...ingredients]);
                            });
                          }}
                        />
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Amount"
                        value={ingredient.amount}
                        variant="outlined"
                        sx={{ width: 100 }}
                        onChange={(event) => {
                          if (event.target.value === "") {
                            ingredient.amount = 0;
                            setIngredients([...ingredients]);
                            return;
                          }

                          ingredient.amount = myParseFloat(event.target.value);
                          setIngredients([...ingredients]);
                        }}
                      />
                      <Select
                        sx={{ width: 100 }}
                        size="small"
                        value={ingredient.unit || "none"}
                        onChange={(event) => {
                          ingredient.unit = event.target.value;
                          setIngredients([...ingredients]);
                        }}
                      >
                        {Object.keys(units).map((unit) => {
                          return (
                            <MenuItem key={unit} value={unit}>
                              {t(`units.${unit}`)}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      <DeleteButton
                        onClick={() => {
                          setIngredients(
                            ingredients.filter(
                              (item) => item.id !== ingredient.id,
                            ),
                          );
                        }}
                      />
                    </Paper>
                  );
                })}
              </Stack>

              {ingredients.length === 0 && !isLoadingIngredients && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    color: "text.secondary",
                    borderRadius: 1,
                    border: "2px dashed",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography variant="body2">
                    No ingredients added yet. Click &quot;Add Ingredient&quot;
                    to get started.
                  </Typography>
                </Box>
              )}

              {isLoadingIngredients && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    color: "text.secondary",
                    borderRadius: 1,
                    border: "2px dashed",
                    borderColor: "grey.300",
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2">
                      Searching ingredients...
                    </Typography>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
          {/* Instructions Card */}
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  Instructions ({instructions.length} steps)
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setInstructions([
                      ...instructions,
                      { id: nanoid(), instruction: "", duration: undefined },
                    ]);
                  }}
                  color="primary"
                  size="small"
                >
                  Add Step
                </Button>
              </Box>

              <Stack spacing={1.5}>
                {instructions.map((step, idx) => (
                  <Paper
                    key={step.id || idx}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={`${idx + 1}`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 40 }}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder={`Describe step ${idx + 1}...`}
                      value={step.instruction}
                      onChange={(e) => {
                        const newInstructions = [...instructions];
                        newInstructions[idx].instruction = e.target.value;
                        setInstructions(newInstructions);
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      placeholder="Duration (sec)"
                      value={step.duration || ""}
                      onChange={(e) => {
                        const newInstructions = [...instructions];
                        newInstructions[idx].duration =
                          e.target.value === ""
                            ? undefined
                            : parseInt(e.target.value, 10);
                        setInstructions(newInstructions);
                      }}
                      sx={{ width: 130 }}
                    />
                    <DeleteButton
                      onClick={() => {
                        setInstructions(
                          instructions.filter((_, i) => i !== idx),
                        );
                      }}
                    />
                  </Paper>
                ))}
              </Stack>

              {instructions.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    color: "text.secondary",
                    borderRadius: 1,
                    border: "2px dashed",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography variant="body2">
                    No instructions added yet. Click &quot;Add Step&quot; to get
                    started.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={() => {
            if (onClose) {
              onClose();
            }
          }}
          size="large"
          sx={{ px: 3 }}
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={() => {
            if (onClose) {
              // CRITICAL: When editing, ALWAYS use recipe.id from prop to prevent duplicates
              // NEVER use state id when editing - it might be stale or incorrect
              let recipeId;
              if (recipe && recipe.id) {
                // Editing mode: MUST use recipe.id from prop
                recipeId = recipe.id;
                console.log("RecipeDialog: Save - Using recipe.id from prop:", recipeId, {
                  stateId: id,
                  recipeId: recipe.id,
                  match: id === recipe.id
                });
                
                // Safety check: warn if state ID doesn't match recipe ID
                if (id !== recipe.id) {
                  console.error(
                    "RecipeDialog: CRITICAL - State ID doesn't match recipe ID!",
                    { 
                      stateId: id, 
                      recipeId: recipe.id, 
                      using: recipe.id,
                      recipe: recipe
                    }
                  );
                }
              } else {
                // Creating mode: use state id (which was generated)
                recipeId = id;
                console.log("RecipeDialog: Save - Creating new recipe with ID:", recipeId);
              }
              
              // CRITICAL: Preserve isArchived status when editing to prevent un-archiving
              // Only set to false when creating a new recipe
              const isArchivedValue = recipe?.isArchived ?? false;
              
              // Safety check: prevent editing archived recipes
              if (recipe && recipe.isArchived) {
                console.error("RecipeDialog: Attempted to edit archived recipe!", {
                  id: recipe.id,
                  name: recipe.name
                });
                alert("Cannot edit archived recipes. Please un-archive the recipe first.");
                return;
              }
              
              const recipeData = {
                id: recipeId,
                name,
                mealType,
                isPublic,
                description,
                instructions,
                kCals,
                servings,
                proteins,
                carbs,
                fats,
                fiber,
                ingredients,
                isArchived: isArchivedValue, // Preserve original archived status
                updatedOn: new Date(),
                createdOn: recipe ? recipe.createdOn : new Date(),
              };
              
              console.log("RecipeDialog: Saving recipe", {
                isEditing: !!recipe,
                recipeId: recipeData.id,
                originalRecipeId: recipe?.id,
                stateId: id,
                mealType: recipeData.mealType,
                recipeData: recipeData
              });
              
              onClose(recipeData, imageUrl);
            }
          }}
          disabled={!name.trim() || ingredients.length === 0}
          size="large"
          sx={{ px: 3 }}
        >
          {recipe ? "Update Recipe" : "Create Recipe"}
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeDialog;

